import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircle, X, Send } from 'lucide-react';

const SupportTicketModal = ({ isOpen, onClose }) => {
  const [supportTicket, setSupportTicket] = useState({
    subject: '',
    priority: 'medium',
    category: 'general',
    message: '',
    userEmail: '',
    userName: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get backend URL from environment
  const backendUrl = `${process.env.REACT_APP_BACKEND_URL}/api` || 'http://localhost:8001';

  const handleSubmitSupportTicket = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Check if user is logged in (has token)
      const token = localStorage.getItem('token');
      
      let response;
      
      if (token) {
        // Authenticated user - use multipart form endpoint
        const formData = new FormData();
        formData.append('subject', supportTicket.subject);
        formData.append('category', supportTicket.category);
        formData.append('message', supportTicket.message);
        formData.append('userEmail', supportTicket.userEmail || 'user@example.com');
        formData.append('userName', supportTicket.userName || 'User');
        
        response = await fetch(`${backendUrl}/support/ticket`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      } else {
        // Non-authenticated user - use public JSON endpoint
        response = await fetch(`${backendUrl}/support/ticket/public`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            subject: supportTicket.subject,
            priority: supportTicket.priority,
            category: supportTicket.category,
            message: supportTicket.message,
            userEmail: supportTicket.userEmail,
            userName: supportTicket.userName
          })
        });
      }

      if (response.ok) {
        // Success - show confirmation
        alert('Support ticket submitted successfully! We\'ll get back to you soon.');
        setSupportTicket({
          subject: '',
          priority: 'medium',
          category: 'general',
          message: '',
          userEmail: '',
          userName: ''
        });
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to submit support ticket');
      }
    } catch (error) {
      console.error('Error submitting support ticket:', error);
      alert('Error submitting support ticket. Please try again or contact us directly at admin@theequalizer.ai');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <MessageCircle className="w-6 h-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-black">Contact Support</h2>
              <p className="text-sm text-gray-600">We're here to help with any questions or issues</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="hover:bg-gray-100"
          >
            <X className="w-5 h-5 text-black" />
          </Button>
        </div>

        <form onSubmit={handleSubmitSupportTicket} className="flex-1">
          <div className="p-6 space-y-6">{/* User Info for non-logged in users */}
          {!localStorage.getItem('token') && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Your Name
                </label>
                <Input
                  type="text"
                  value={supportTicket.userName}
                  onChange={(e) => setSupportTicket({...supportTicket, userName: e.target.value})}
                  placeholder="Enter your name"
                  className="text-black"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={supportTicket.userEmail}
                  onChange={(e) => setSupportTicket({...supportTicket, userEmail: e.target.value})}
                  placeholder="Enter your email"
                  className="text-black"
                  required
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Subject
            </label>
            <Input
              type="text"
              value={supportTicket.subject}
              onChange={(e) => setSupportTicket({...supportTicket, subject: e.target.value})}
              placeholder="Brief description of your issue"
              className="text-black"
              required
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Category
              </label>
              <select
                value={supportTicket.category}
                onChange={(e) => setSupportTicket({...supportTicket, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-black"
              >
                <option value="general">General Question</option>
                <option value="technical">Technical Issue</option>
                <option value="billing">Billing & Subscription</option>
                <option value="legal_guidance">Guidance</option>
                <option value="document_generation">Document Generation</option>
                <option value="account">Account Issues</option>
                <option value="feature_request">Feature Request</option>
                <option value="bug_report">Bug Report</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Priority
              </label>
              <select
                value={supportTicket.priority}
                onChange={(e) => setSupportTicket({...supportTicket, priority: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-black"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Message
            </label>
            <textarea
              value={supportTicket.message}
              onChange={(e) => setSupportTicket({...supportTicket, message: e.target.value})}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-black"
              placeholder="Please describe your issue or question in detail..."
              required
            />
          </div>
          </div>
          
          <div className="p-6 bg-gray-50 border-t flex items-center justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Ticket
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportTicketModal;